import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Dispatch } from 'src/config/rootStore';
import { navigationService } from 'src/config/rootService';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { color } from 'src/shared/styles/variables';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import {
    BodyTextMedium,
    Button,
    ButtonGroup,
    HeadingText20,
    HeadingText50,
    IconButton,
    pxToRem,
} from '@modernhealth/vital-components';
import { useHistory } from 'react-router-dom';

const ActionContainer = styled.div({
    paddingTop: '54px',
    textAlign: 'center',
    width: '100%',
});
const Content = styled.div({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    flexDirection: 'column',
    maxWidth: '500px',
    flexGrow: 1,
});
const Container = styled.div({
    display: 'flex',
    paddingTop: '72px',
    justifyContent: 'center',
    alignItems: 'start',
    flexGrow: 1,
});

const CurrentEmail = styled.div({
    backgroundColor: color.gray05,
    padding: pxToRem(16),
    marginTop: pxToRem(22),
    marginBottom: pxToRem(24),
    width: '100%',
});

const BackButton = styled(IconButton)({
    '&&': {
        border: 'none',
        justifyContent: 'flex-start',
    },
});

export enum PreferredEmailPageTestId {
    EmailField = 'PreferredEmailPageTestId.EmailField',
    ChangeEmailButton = 'PreferredEmailPageTestId.ChangeEmailButton',
    ContinueButton = 'PreferredEmailPageTestId.SubmitButton',
}
export const PreferredEmailSentPage: React.FC = () => {
    const [emailSent, setEmailSent] = useState(false);
    const history = useHistory();
    const user = useSelector(getUserInformation);
    const dispatch: Dispatch = useDispatch();
    const { t } = useTranslation();
    const helpCenterUrl = t('user:help_center.complete_url');

    useEffect(() => {
        if (user.ssoOnly) {
            navigationService.navigate('/completion-page');
        }
    }, [user.ssoOnly]);

    const handleSubmit = () => {
        if (emailSent) {
            window.open(helpCenterUrl, '_blank');
        } else {
            setEmailSent(true);
            dispatch.user.updateEmailAddress(user.newEmailAddressNotConfirmed);
        }
    };

    // If a member is SSO-only we just plan on redirecting the member so no point in showing this screen
    if (user.ssoOnly) {
        return <CenteredSpinner />;
    }

    return (
        <>
            <Container>
                <Content>
                    <BackButton
                        title="back"
                        icon="arrow-left"
                        onClick={() => history.push('/preferred-email')}
                        size="large"
                    />
                    <HeadingText50 marginBottom={26} marginTop={20}>
                        {t('auth:preferred_email_sent.title')}
                    </HeadingText50>
                    <BodyTextMedium>
                        {t('auth:preferred_email_sent.verification_sent')}
                    </BodyTextMedium>

                    <CurrentEmail>
                        <HeadingText20>{user.newEmailAddressNotConfirmed}</HeadingText20>
                    </CurrentEmail>

                    <BodyTextMedium>
                        {t('auth:preferred_email_sent.click_to_finish')}
                    </BodyTextMedium>
                    <ActionContainer>
                        <ButtonGroup>
                            <Button
                                onClick={() => history.push('/preferred-email')}
                                variant="secondary"
                                size="large"
                                fluid
                            >
                                {t('auth:preferred_email_sent.change_email')}
                            </Button>
                            <Button type="submit" size="large" onClick={handleSubmit} fluid>
                                {emailSent
                                    ? t('auth:preferred_email_sent.need_help')
                                    : t('auth:preferred_email_sent.resend_email')}
                            </Button>
                        </ButtonGroup>
                    </ActionContainer>
                </Content>
            </Container>
        </>
    );
};
